// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, HostBinding, NgZone } from '@angular/core';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMListComponent } from '@list/lm-list.component';
import { LMActionService } from '@services/lm-action.service';
import { LMDashboardPanelBaseComponent } from './lm-dashboard-panel-base.component';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { AppConstants } from '@utils/app.constants';
import { Observable, Observer, of } from 'rxjs';
import { LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';

import * as urltemplate from 'url-template';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMGlobalUtilService } from '@utils/lm-global-util.service';
import {
  LMContextService,
  LMErrorUtilService,
  LMOverlayService,
  LMUrlInfoBuilderService,
  LMUrlUtilService
} from '@services/index';
import { LMNavigationService, LMRefreshRequest } from '@app/components';
import { LMGlobalPersonalizationService } from '@app/states';
import ClickEvent = JQuery.ClickEvent;

@Component({
  selector: 'lm-dashboard-panel-list',
  standalone: false,
  templateUrl: './lm-dashboard-panel-list.component.html',
  styleUrls: [ 'lm-dashboard-panel-list.component.scss' ]
  })
export class LMDashboardPanelListComponent extends LMDashboardPanelBaseComponent implements OnInit {
  @HostBinding('attr.id') public id;
  @HostBinding('attr.data-automation-id') public automationId;

  @ViewChild('LMListComponent', { static: true }) lmListComponent: LMListComponent;

  @Input() panel: LandmarkPanel;
  @Input() messageRuleView: LMDataView;

  @Input() set fromDataView(dataView) {
    this._dataView = dataView;
    this.build();
    this.calculateShowCreate();
  }

  get fromDataView(): LMDataView {
    return this._dataView;
  }

  @Output() componentDisplayed = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();

  private _defaultCreateAction: LMActionSpec;
  private _showCreateAction = false;
  private _dataView: LMDataView;

  public listUrlInfo: LMListUrlInfo;
  public createActionLabel: string;
  public overNavigationLink: string;
  public hasOverviewNavigation = false;
  public displayLinkAsText = false;
  public showPagerInDashboard = false;
  public contentClasses = 'widget-content';
  public testVersion1: boolean;
  public isInAccessibilityMode: boolean;
  public drillAroundWindow = false;

  constructor(
    private   lmActionService: LMActionService,
    private   lmContextService: LMContextService,
    private   lmFormatUtilService: LMFormatUtilService,
    private   lmGlobalUtilService: LMGlobalUtilService,
    protected lmThemeService: LMThemeService,
    private   lmUrlUtilBuilderService: LMUrlInfoBuilderService,
    private   lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    protected lmOverlayService: LMOverlayService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmNavigationService: LMNavigationService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected zone: NgZone
  ) {
    super(lmThemeService, lmOverlayService, lmUrlUtilService, lmNavigationService, lmErrorUtilService, zone);
  }

  ngOnInit() {
    this.isInAccessibilityMode = this.lmGlobalPersonalizationService.getAccessibilityMode();
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;

    this.panel.listSpec.busListSpec.showAsSimpleList = true;
    this.panel.listSpec.busListSpec.selection = this.panel.listSpec?.busListSpec?.selection === 'disabled' ? 'disabled' : 'single';
    this.panel.listSpec.listType = 'dashboard';
    this.id = this.panel.id + AppConstants.ELEMENT_ID_SUFFIX_DASHBOARDPANEL;
    this.automationId = `lm-${this.panel.automationId}-dashboardpanel`;

    if (this.panel.displayChart) {
      this.panel.listSpec.busListSpec.defaultChartName = this.panel.displayChart;
    }

    this.listUrlInfo = this.lmUrlUtilBuilderService.getListUrlInfoFromListSpec(
      this.panel.listSpec, { pagePaneSpec: { relationResourceId: this.panel?.relationResourceId },
      isArchiveData: this.panel.busFormPanel?.isArchiveData || this.panel.isArchiveData });
    if (this.createAction) {
      this._showCreateAction = true;
    }

    if (this.lmContextService.drillAroundWindow()) {
      this.drillAroundWindow = true;
    }

    this.build();
  }

  saveDashboardPanelState(): void {
    if (this.lmListComponent) {
      this.lmListComponent.saveComponentState(true);
    }
  }

  clearDashboardPanelState(): void {
    if (this.lmListComponent) {
      this.lmListComponent.clearComponentState(true);
    }
  }

  isModified(): boolean {
    if (this.lmListComponent) {
      return this.lmListComponent.isModified();
    }
    return false;
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean>  {
    if (this.lmListComponent) {
      return this.lmListComponent.refresh(request);
    } else {
      return of(true);
    }
  }

  save(callBack: Observer<boolean>) {
    if (this.lmListComponent && this.lmListComponent.isModified()) {
      this.lmListComponent.runSaveAction(AppConstants.STANDARD_SAVE, callBack);
    } else {
      callBack.next(true);
      callBack.complete();
    }
  }

  public get createAction() {
    return this._defaultCreateAction;
  }

  public get showCreateAction() {
    return this._showCreateAction;
  }

  build() {
    if (this.panel && !this.drillAroundWindow) {
      if (this.panel.overviewNavigation || (this.panel.busFormPanel && this.panel.busFormPanel.overviewNavigation)) {
        this.hasOverviewNavigation = true;
        if ((this.panel.overviewNavigation && this.panel.overviewNavigation.label)
          || (this.panel.busFormPanel && this.panel.busFormPanel.overviewNavigation && this.panel.busFormPanel.overviewNavigation.label)
        ) {
          this.displayLinkAsText = true;
          this.overViewNavigationLabel();
        }
      }
    }
    this._setContentClasses();
  }

  overViewNavigationLabel() {
    const message = this.panel.overviewNavigation ? this.panel.overviewNavigation.label : this.panel.busFormPanel.overviewNavigation.label;
    const dataView = this.fromDataView ? this.fromDataView : this.messageRuleView;
    this.overNavigationLink = this.lmFormatUtilService.formatMessageRule(message, dataView);
  }

  onListDisplayed() {
    this.showPagerInDashboard = this.lmListComponent.showPagingBarInDashboardPanel();

    this.calculateShowCreate();
    this.componentDisplayed.emit({ panel: this.panel });
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  public onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  public onPanelTransactionEvent(event: LMPanelTransactionEvent) {
    this.panelTransactionEvent.emit(event);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    this.drillAroundEvent.emit(event);
  }

  onCreateClicked() {
    this.lmListComponent.runCreateAction(this._defaultCreateAction);
  }

  // List navigation doesn't seem to be implemented yet?
  public onNavigationClick($event: ClickEvent) {
    const navigation: LMOverviewNavigation = this.panel.overviewNavigation ? this.panel.overviewNavigation : this.panel.busFormPanel.overviewNavigation;
    let flyoutOriginatingEvent: LMFormOverlayClickedEvent = null;

    const flyoutEnabled = this.lmContextService.allowFlyoutLinks();
    if (flyoutEnabled && navigation.openInOverlay) {
        flyoutOriginatingEvent = {
          event: $event,
          row: 0
        };

    }
    this.runNavigation(navigation, flyoutOriginatingEvent);
  }

  private runNavigation(navigation: LMOverviewNavigation, flyoutOriginatingEvent: LMFormOverlayClickedEvent): void {
    let dataView;
    if (this.fromDataView) {
      dataView = this.fromDataView;
    } else {
      dataView = this.lmListComponent.getDataViewSet().header;
    }

    const compiledUrlTemplate = urltemplate.parse(this.listUrlInfo.navigationUrl);
    let url = compiledUrlTemplate.expand({ resourceId: dataView.resourceId, navigationName: navigation.name });

    if (this.lmListComponent.listSpec &&
      this.lmListComponent.listSpec.busListSpec &&
      this.lmListComponent.listSpec.busListSpec.implementsCubeView) {

      let delimiter = (url.indexOf('?') > -1) ? '&' : '?';
      if (this.panel.overviewNavigation.overrideDimensionTree.length > 0) {
        url = url + delimiter + 'dimensionsOverride=' + this.panel.overviewNavigation.overrideDimensionTree.join(',');
        delimiter = '&';
      }
      if (this.panel.displayChart) {
        url = url + delimiter + 'chartName=' + this.panel.displayChart;
      }
    }

    if (this.lmContextService.allowFlyoutLinks() && navigation.openInOverlay) {
      this.openNavigationInOverlay(url, navigation.name, navigation.navigationTargetComponent, flyoutOriginatingEvent);
    } else {
      this.lmGlobalUtilService.navigateByUrl(url);
    }
  }

  private _setContentClasses() {
    if (this.panel && this.panel.widgetHeight) {
      this.contentClasses += ' widget-height-' + this.panel.widgetHeight;
    }
  }

  private calculateShowCreate(): void {
    if ( this.lmListComponent?.listSpec?.busListSpec &&
        !this.lmListComponent?.listSpec?.busListSpec?.implementsCubeView) {

      const dataViewSet = this.lmListComponent.getDataViewSet();

      const actions = this.lmListComponent.listSpec.busClassSpec.actions;
      const actionIndexes = dataViewSet.actionIndexes;
      this._defaultCreateAction = this.lmActionService.getDefaultCreateAction(actions, actionIndexes);

      if (this._defaultCreateAction) {
        this.createActionLabel = this._defaultCreateAction.label;
        this._showCreateAction = true;
      } else {
        this._showCreateAction = false;
      }
    }
  }
}
